/**
 * Generic Record Storage Service
 *
 * Provides generic CRUD operations for records of any object type.
 * Uses localStorage with key pattern: crm_records_${objectApiName}
 * When USE_GOBODO_API is enabled, routes to Gobodo backend API.
 */

import {
  createGobodoRecord,
  createGobodoRecords,
  getGobodoRecords,
  getGobodoRecordById,
  getGobodoRecordByKey,
  getGobodoRecordCount,
  updateGobodoRecord,
  deleteGobodoRecord,
  type CreateGobodoRecordsOptions,
  type GobodoCacheOpts,
} from './gobodo/gobodoRecordService';
import { makeObjectByKeyReadCacheOpts } from './gobodo/gobodoCacheConfig';
import GobodoConfig from '../config/gobodoConfig';
import { ensureGobodoCustomSuffixOnRecordKey } from '../utils/gobodoRecordKeySuffix';
import { getRdsRecords, getRdsRecordById, getRdsRecordCount } from './rdsApiClient';
import { USE_GOBODO_API } from '../utils/gobodoUtils';
import { USE_RDS_FOR_CORE_OBJECTS, isRdsCoreObject } from '../config/rdsConfig';
import { normalizeToGobodoFormat } from '../utils/objectApiNameNormalizer';
import { triggerWorkflows } from './workflowExecution/workflowTriggerService';
import { format, getISOWeek, getISOWeekYear, parseISO, startOfWeek } from 'date-fns';

export interface GenericRecord {
  id: string;
  [key: string]: any; // Allow any fields
}

const CYBERSWEEP_MEETING_AGENDA_OBJECT_BASENAMES = new Set(['cybersweepmeetingagenda']);

function isCyberSweepMeetingAgendaObject(objectApiName: string): boolean {
  const normalized = normalizeToGobodoFormat(objectApiName || '');
  const base = normalized.replace(/(__stg3_c|__stg_c|__dev_c|__pp2_c|__pp_c|__c)$/i, '').toLowerCase();
  return CYBERSWEEP_MEETING_AGENDA_OBJECT_BASENAMES.has(base);
}

function coerceToDate(value: unknown): Date | null {
  if (value instanceof Date && !isNaN(value.getTime())) return value;
  if (typeof value === 'string' && value.trim()) {
    const raw = value.trim();
    const parsed = /^\d{4}-\d{2}-\d{2}$/.test(raw) ? parseISO(raw) : new Date(raw);
    return !isNaN(parsed.getTime()) ? parsed : null;
  }
  return null;
}

function deriveCyberSweepMeetingAgendaDefaults(recordData: Record<string, any>): Record<string, any> {
  const meetingDate = coerceToDate(recordData.Meeting_Date__c ?? recordData.meetingDate ?? recordData.meetingDate__c);
  if (!meetingDate) return recordData;

  const weekStartDate = startOfWeek(meetingDate, { weekStartsOn: 1 });
  const isoYear = getISOWeekYear(weekStartDate);
  const isoWeek = String(getISOWeek(weekStartDate)).padStart(2, '0');
  const generatedName = `MA-${isoYear}-W${isoWeek}`;

  return {
    ...recordData,
    Meeting_Date__c: recordData.Meeting_Date__c ?? format(meetingDate, 'yyyy-MM-dd'),
    Week_Start_Date__c: recordData.Week_Start_Date__c ?? format(weekStartDate, 'yyyy-MM-dd'),
    Name__c: recordData.Name__c ?? generatedName,
    name: recordData.name ?? generatedName,
    label: recordData.label ?? generatedName,
  };
}

function applyCreateRecordDefaults(objectApiName: string, recordData: Record<string, any>): Record<string, any> {
  if (isCyberSweepMeetingAgendaObject(objectApiName)) {
    return deriveCyberSweepMeetingAgendaDefaults(recordData);
  }
  return recordData;
}

/**
 * Get storage key for an object type
 */
function getStorageKey(objectApiName: string): string {
  return `crm_records_${objectApiName}`;
}

/**
 * Generate a unique record ID
 */
function generateRecordId(objectApiName: string): string {
  const timestamp = Date.now();
  const random = Math.random().toString(36).substr(2, 9);
  return `rec_${objectApiName.toLowerCase()}_${timestamp}_${random}`;
}

/** Max records to fetch when using "get all" style calls (dashboard, nav, search, dropdowns). Use useRecordCount for totals. Keep small; use UI pagination (getRecordsForObjectPage) for lists. */
const DEFAULT_FETCH_SIZE = 50;
const MAX_CASE_FETCH_SIZE = 300;

/** Modest server-side cache for table page requests (gobodo-api-promise expects seconds). */
const TABLE_PAGE_CACHE_DURATION_SECONDS = 90;

/**
 * Get records for an object type (capped at DEFAULT_FETCH_SIZE when using backend).
 * Use getRecordCount() or useRecordCount() for totals; use getRecordsForObjectPage() for paginated lists.
 * @param options.size - Override fetch size (e.g. 50 for faster initial load)
 * @param options.cache - Optional Gobodo server-side cache options (cachekey, cacheduration, etc.)
 */
export async function getRecordsForObject(
  objectApiName: string,
  options?: { size?: number; cache?: GobodoCacheOpts }
): Promise<GenericRecord[]> {
  const requestedSize = options?.size ?? DEFAULT_FETCH_SIZE;
  const normalizedObject = normalizeToGobodoFormat(objectApiName);
  const isCaseObject = /^Case(?:__|$)/i.test(normalizedObject);
  const size = isCaseObject ? Math.min(requestedSize, MAX_CASE_FETCH_SIZE) : requestedSize;
  if (USE_RDS_FOR_CORE_OBJECTS && isRdsCoreObject(objectApiName)) {
    return getRdsRecords(objectApiName, { page: 0, size });
  }
  if (USE_GOBODO_API) {
    const result = await getGobodoRecords(normalizedObject, { page: 0, size, cache: options?.cache });
    return result.records;
  }

  try {
    const storageKey = getStorageKey(objectApiName);
    const stored = localStorage.getItem(storageKey);
    return stored ? JSON.parse(stored) as GenericRecord[] : [];
  } catch (error) {
    console.error(`Failed to get records for ${objectApiName}:`, error);
    return [];
  }
}

/**
 * Get one page of records and total count (for server-side pagination).
 * When USE_RDS_FOR_CORE_OBJECTS and object is core, uses RDS API with count.
 * When USE_GOBODO_API, uses Gobodo records + count API.
 * Otherwise reads from localStorage and slices.
 */
export async function getRecordsForObjectPage(
  objectApiName: string,
  options: { page: number; size: number }
): Promise<{ records: GenericRecord[]; totalRecords: number }> {
  const { page, size } = options;
  const offset = page * size;

  if (USE_RDS_FOR_CORE_OBJECTS && isRdsCoreObject(objectApiName)) {
    if (process.env.NODE_ENV === 'development') {
      console.log('[getRecordsForObjectPage] Using RDS path; requesting records + count for', objectApiName);
    }
    const [records, totalRecords] = await Promise.all([
      getRdsRecords(objectApiName, { page, size }),
      getRdsRecordCount(objectApiName),
    ]);
    return { records, totalRecords };
  }

  if (USE_GOBODO_API) {
    // Prefer count API for correct pagination totals, but do not fail the page request when count times out.
    if (process.env.NODE_ENV === 'development') {
      console.log('[getRecordsForObjectPage] Using Gobodo path; requesting records + count (getObjectCountByTypeInGraph) for', objectApiName);
    }
    const normalized = normalizeToGobodoFormat(objectApiName);
    const tablePageCache: GobodoCacheOpts = {
      cachekey: ['gobodo-records-page', normalized, String(page), String(size)],
      cacheduration: String(TABLE_PAGE_CACHE_DURATION_SECONDS),
    };
    const result = await getGobodoRecords(normalized, { page, size, cache: tablePageCache });
    let totalRecords: number;
    try {
      totalRecords = await getGobodoRecordCount(objectApiName);
    } catch {
      // Fail-open count approximation: enough for pagination to continue instead of blanking.
      const recordsLen = result.records.length;
      totalRecords = recordsLen < size ? offset + recordsLen : (page + 1) * size + 1;
    }
    return { records: result.records, totalRecords };
  }

  try {
    const storageKey = getStorageKey(objectApiName);
    const stored = localStorage.getItem(storageKey);
    const all: GenericRecord[] = stored ? (JSON.parse(stored) as GenericRecord[]) : [];
    const totalRecords = all.length;
    const records = all.slice(offset, offset + size);
    return { records, totalRecords };
  } catch (error) {
    console.error(`Failed to get records page for ${objectApiName}:`, error);
    return { records: [], totalRecords: 0 };
  }
}

/**
 * Get total record count for an object type (count endpoint only, no record fetch).
 * Use this for count-only UI (badges, totals) to avoid fetching full lists.
 */
export async function getRecordCount(objectApiName: string): Promise<number> {
  if (USE_RDS_FOR_CORE_OBJECTS && isRdsCoreObject(objectApiName)) {
    return getRdsRecordCount(objectApiName);
  }
  if (USE_GOBODO_API) {
    return getGobodoRecordCount(normalizeToGobodoFormat(objectApiName));
  }
  try {
    const storageKey = getStorageKey(objectApiName);
    const stored = localStorage.getItem(storageKey);
    const all: GenericRecord[] = stored ? (JSON.parse(stored) as GenericRecord[]) : [];
    return all.length;
  } catch {
    return 0;
  }
}

/** Options for {@link getRecordById} when the backend is Gobodo. */
export interface GetRecordByIdOptions {
  /**
   * Gobodo `GET /object/{numericId}` still accepts a **single** `cachekey` query segment. Pass the
   * record’s object `key` so read-through cache lines match `getObjectByKey` / property PATCH (same
   * key the server uses for CS_Audit, e.g. `001Nq00000DN9CoIAL__stg5_c` instead of only `1029531`).
   */
  gobodoObjectKeyForGetByIdCache?: string;
}

/**
 * Get a single record by ID.
 * When USE_RDS_FOR_CORE_OBJECTS and object is core, reads from RDS API.
 * Otherwise when Gobodo is on, fetches from Gobodo.
 */
export async function getRecordById(
  objectApiName: string,
  recordId: string,
  options?: GetRecordByIdOptions
): Promise<GenericRecord | null> {
  if (USE_RDS_FOR_CORE_OBJECTS && isRdsCoreObject(objectApiName)) {
    return getRdsRecordById(objectApiName, recordId);
  }
  if (USE_GOBODO_API) {
    const gobodoGetCache: GobodoCacheOpts | undefined =
      options?.gobodoObjectKeyForGetByIdCache != null && String(options.gobodoObjectKeyForGetByIdCache).trim()
        ? makeObjectByKeyReadCacheOpts(
            ensureGobodoCustomSuffixOnRecordKey(String(options.gobodoObjectKeyForGetByIdCache).trim()),
            GobodoConfig.graph()
          )
        : undefined;
    const byId = await getGobodoRecordById(recordId, gobodoGetCache);
    if (byId) return byId;
    if (/^[0-9]+$/.test(recordId)) return null;
    return (await getGobodoRecordByKey(recordId)) ?? null;
  }

  try {
    const records = await getRecordsForObject(objectApiName);
    return records.find(r => r.id === recordId) || null;
  } catch (error) {
    console.error(`Failed to get record ${recordId} for ${objectApiName}:`, error);
    return null;
  }
}

/**
 * Create a new record.
 * When Gobodo is on, throws on failure (no silent localStorage fallback).
 */
export async function createRecord(objectApiName: string, recordData: Record<string, any>): Promise<GenericRecord> {
  const normalizedRecordData = applyCreateRecordDefaults(objectApiName, recordData);
  let newRecord: GenericRecord;

  if (USE_GOBODO_API) {
    newRecord = await createGobodoRecord(normalizeToGobodoFormat(objectApiName), normalizedRecordData);
  } else {
    const storageKey = getStorageKey(objectApiName);
    const records = await getRecordsForObject(objectApiName);
    newRecord = {
      ...normalizedRecordData,
      id: normalizedRecordData.id || generateRecordId(objectApiName),
      createdDate: normalizedRecordData.createdDate || new Date().toISOString(),
      lastModified: new Date().toISOString(),
      createdBy: normalizedRecordData.createdBy || 'current_user'
    };
    records.push(newRecord);
    localStorage.setItem(storageKey, JSON.stringify(records));
  }

  // Fire-and-forget workflow evaluation
  triggerWorkflows(objectApiName, newRecord, null, 'create').catch(() => {});

  return newRecord;
}

export type CreateRecordsBatchOptions = CreateGobodoRecordsOptions;

/**
 * Create multiple records in one batched Gobodo request (chunked) or sequential localStorage inserts.
 * Triggers create workflows per created row. When Gobodo is on, earlier chunks may persist if a later chunk fails.
 */
export async function createRecordsBatch(
  objectApiName: string,
  recordDataList: Record<string, any>[],
  options?: CreateRecordsBatchOptions
): Promise<GenericRecord[]> {
  if (recordDataList.length === 0) {
    return [];
  }

  const normalizedForGobodo = normalizeToGobodoFormat(objectApiName);
  const normalizedRecordDataList = recordDataList.map((recordData) =>
    applyCreateRecordDefaults(objectApiName, recordData)
  );
  let created: GenericRecord[];

  if (USE_GOBODO_API) {
    created = await createGobodoRecords(normalizedForGobodo, normalizedRecordDataList, options);
  } else {
    const storageKey = getStorageKey(objectApiName);
    const records = await getRecordsForObject(objectApiName);
    const now = new Date().toISOString();
    created = normalizedRecordDataList.map((recordData) => {
      const newRecord: GenericRecord = {
        ...recordData,
        id: recordData.id || generateRecordId(objectApiName),
        createdDate: recordData.createdDate || now,
        lastModified: now,
        createdBy: recordData.createdBy || 'current_user',
      };
      records.push(newRecord);
      return newRecord;
    });
    localStorage.setItem(storageKey, JSON.stringify(records));
  }

  for (const row of created) {
    triggerWorkflows(objectApiName, row, null, 'create').catch(() => {});
  }

  return created;
}

export interface UpdateRecordOptions {
  /** When Gobodo is on, send only these fields via setObjectPropertiesById / setObjectPropertiesByKey instead of merge + updateObject. */
  gobodoPropertyPatch?: boolean;
}

/**
 * Update an existing record.
 * When Gobodo is on, throws on failure (no silent localStorage fallback).
 */
export async function updateRecord(
  objectApiName: string,
  recordId: string,
  updates: Record<string, any>,
  options?: UpdateRecordOptions
): Promise<GenericRecord | null> {
  console.group('[recordStorageService.updateRecord] START');
  console.log('objectApiName:', objectApiName);
  console.log('recordId:', recordId);
  console.log('updates:', updates);
  console.log('USE_GOBODO_API:', USE_GOBODO_API);
  console.log('Timestamp:', new Date().toISOString());

  // Capture previous record state for field_changed triggers
  let previousRecord: GenericRecord | null = null;
  try {
    console.log('[updateRecord] Fetching previous record...');
    previousRecord = await getRecordById(objectApiName, recordId);
    console.log('[updateRecord] Previous record fetched:', previousRecord ? 'found' : 'not found');
  } catch (err) {
    console.log('[updateRecord] Failed to fetch previous record (non-critical):', err);
    // Non-critical — workflows will still run, just without field change detection
  }

  let updatedRecord: GenericRecord | null;

  if (USE_GOBODO_API) {
    console.log('[updateRecord] Calling updateGobodoRecord...');
    try {
      updatedRecord = await updateGobodoRecord(recordId, updates, {
        gobodoUpdateMode: options?.gobodoPropertyPatch ? 'propertyPatch' : 'fullObject',
      });
      console.log('[updateRecord] updateGobodoRecord returned:', updatedRecord ? 'success' : 'null');
    } catch (gobodoError) {
      console.error('[updateRecord] updateGobodoRecord THREW ERROR:', gobodoError);
      console.groupEnd();
      throw gobodoError;
    }
  } else {
    console.log('[updateRecord] Using localStorage fallback...');
    const storageKey = getStorageKey(objectApiName);
    const records = await getRecordsForObject(objectApiName);
    const index = records.findIndex(r => r.id === recordId);
    if (index === -1) {
      console.log('[updateRecord] Record not found in localStorage');
      console.groupEnd();
      return null;
    }

    updatedRecord = { ...records[index], ...updates, lastModified: new Date().toISOString() };
    records[index] = updatedRecord;
    localStorage.setItem(storageKey, JSON.stringify(records));
    console.log('[updateRecord] localStorage updated');
  }

  // Fire-and-forget workflow evaluation
  if (updatedRecord) {
    console.log('[updateRecord] Triggering workflows (fire-and-forget)...');
    triggerWorkflows(objectApiName, updatedRecord, previousRecord, 'update').catch(() => {});
  }

  console.log('[updateRecord] COMPLETE - returning record');
  console.groupEnd();
  return updatedRecord;
}

/**
 * Delete a record.
 * When Gobodo is on, throws on failure (no silent localStorage fallback).
 */
export async function deleteRecord(objectApiName: string, recordId: string): Promise<boolean> {
  if (USE_GOBODO_API) {
    return deleteGobodoRecord(recordId);
  }

  const storageKey = getStorageKey(objectApiName);
  const stored = localStorage.getItem(storageKey);
  const records: GenericRecord[] = stored ? JSON.parse(stored) : [];
  const index = records.findIndex(r => r.id === recordId);
  if (index === -1) return false;

  records.splice(index, 1);
  localStorage.setItem(storageKey, JSON.stringify(records));
  return true;
}
